/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/standard-button';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import ModalDialog, { ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';

import { fg } from '@confluence/feature-gating';
import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent';
import { requireLegacyWRM } from '@confluence/wrm/entry-points/requireLegacyWRMHelpers';

type User = {
	userId: string;
	avatarUrl: string;
	displayName: string;
};

type LegacyContentModalProps = {
	onClose: (val: boolean) => void;
	content: string;
	user: User;
	webResources: [];
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	paddingTop: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	paddingLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		paddingTop: token('space.050'),
	},
});

export const LegacyContentModal = ({
	onClose,
	content,
	user,
	webResources,
}: LegacyContentModalProps) => {
	const [containerRef, setContainerRef] = useState<HTMLSpanElement | null>(null);
	useEffect(() => {
		requireLegacyWRM(
			webResources,
			() => {},
			() => {},
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (containerRef) {
			void evalLegacyConnectInlineScripts(containerRef);
		}
	}, [containerRef]);

	const onRefSet = (ref: any) => {
		setContainerRef(ref);
	};

	const handleClose = () => {
		onClose(false);
	};

	const handleImageClick = (event: any) => {
		// clicking on an image shows a full screen modal
		// of the image in the background
		if (event.target instanceof HTMLImageElement) {
			onClose(false);
		}
	};

	return (
		<ModalDialog
			onClose={fg('confluence_frontend_comments_dialogs_close_button') ? handleClose : undefined}
			width="large"
			testId="modal-dialog-legacy-content"
		>
			{fg('confluence_frontend_comments_dialogs_close_button') && <ModalHeader hasCloseButton />}
			<ModalBody>
				<Container>
					<ProfileAvatar userId={user.userId} src={user.avatarUrl} name={user.displayName} />
					<ContentContainer onClick={handleImageClick}>
						<ProfileLink userId={user.userId} fullName={user.displayName} />
						<span ref={onRefSet} dangerouslySetInnerHTML={{ __html: content }} />
					</ContentContainer>
				</Container>
			</ModalBody>
			<ModalFooter>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<Button autoFocus appearance="primary" onClick={handleClose}>
					Close
				</Button>
			</ModalFooter>
		</ModalDialog>
	);
};
